"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const React = require("react");
const query_string_1 = require("query-string");
const QueryParamProvider_1 = require("./QueryParamProvider");
const params_1 = require("./params");
const updateUrlQuery_1 = require("./updateUrlQuery");
/**
 * Given a query param name and query parameter configuration ({ encode, decode })
 * return the decoded value and a setter for updating it.
 *
 * The setter takes two arguments (newValue, updateType) where updateType
 * is one of 'replace' | 'replaceIn' | 'push' | 'pushIn', defaulting to
 * 'replaceIn'.
 *
 * You may optionally pass in a rawQuery object, otherwise the query is derived
 * from the location available in the QueryParamContext.
 *
 * D = decoded type
 * D2 = return value from decode (typically same as D)
 */
exports.useQueryParam = (name, paramConfig = params_1.StringParam, rawQuery) => {
    const { history, location } = React.useContext(QueryParamProvider_1.QueryParamContext);
    // read in the raw query
    if (!rawQuery) {
        rawQuery = React.useMemo(() => location.query ||
            query_string_1.parse(location.search) ||
            {}, [location.query, location.search]);
    }
    // read in the encoded string value
    const encodedValue = rawQuery[name];
    // decode if the encoded value has changed, otherwise
    // re-use memoized value
    const decodedValue = React.useMemo(() => {
        if (encodedValue == null) {
            return undefined;
        }
        return paramConfig.decode(encodedValue);
        // note that we use the stringified encoded value since the encoded
        // value may be an array that is recreated if a different query param
        // changes.
    }, [
        encodedValue instanceof Array
            ? query_string_1.stringify({ name: encodedValue })
            : encodedValue,
    ]);
    // create the setter, memoizing via useCallback
    const setValue = React.useCallback((newValue, updateType) => {
        const newEncodedValue = paramConfig.encode(newValue);
        updateUrlQuery_1.updateUrlQuery({ [name]: newEncodedValue }, location, history, updateType);
    }, [location]);
    return [decodedValue, setValue];
};
